import { extractFHAEvents } from './fha';
import { extractImportedShapefiles } from './importedShapefile';
import { extractOtherImagery } from './otherImagery';
import { extractCropWaterUseEvents } from './cropWaterUse';
import {
  SOIL_TESTS,
  PRESCRIPTION,
  IMPORTED_SHAPEFILE,
  OTHER_IMAGERY,
  SEASON_SPECIFIC_MOSAIC_V3_IDS,
  SEASON_AGNOSTIC_MOSAIC_V3_IDS,
  FHA_KEY,
  CROP_WATER,
} from '../../../constants/mapLayerIds';

/**
 * Placeholder extract function for mosaic layers whose extract functions are still in progress
 * @param {Array} events
 * @return {Array}
 */
function extractPlaceholder(events) {
  return events;
}

// Object of
/**
 * Call the needed Mosaic layer-specific extract function for the given layer ID
 * @param {String} layerId
 * @param {Array} events
 * @return {Any}
 */
function extractLayerFuncs(layerId, events) {
  switch (layerId) {
    case FHA_KEY:
      return extractFHAEvents(events);
    case PRESCRIPTION:
      return extractPlaceholder(events);
    case SOIL_TESTS:
      return extractPlaceholder(events);
    case IMPORTED_SHAPEFILE:
      return extractImportedShapefiles(events);
    case OTHER_IMAGERY:
      return extractOtherImagery(events);
    case CROP_WATER:
      return extractCropWaterUseEvents(events);
    default:
      return null;
  }
}

/**
 * Filter all received Mosaic V3 events for the given layer ID
 * @param {Array} events
 * @param {Array} layerIds
 * @return {Array}
 */
function filterEventsForLayers(events, layerIds) {
  return events.filter(
    (event) => layerIds.includes(event.layer) && !event.deleted
  );
}

/**
 * Filter layers without Mosaic V3 events. Layers with events will have their events extracted
 * (the event's data will be filtered and organized into an expected structure)
 * @param {Array} events - All Mosaic V3 events received
 * @param {Array} layerIds - Mosaic IDs being filtered and extracted
 * @return {Object} - Object with Mosaic IDs as keys, whose values are an object of extracted
 * events
 */
export function extractLayers(events, layerIds) {
  return layerIds.reduce((_layers, layer) => {
    // Filter for events for current layer
    const currLayerEvents = filterEventsForLayers(events, layer.ids);
    if (!currLayerEvents.length) {
      return _layers;
    }

    // Extract layer data (filter and organize the data into an expected structure)
    return {
      ..._layers,
      [layer.key]: extractLayerFuncs(layer.key, currLayerEvents),
    };
  }, {});
}

/**
 * Isolate the Mosaic Layers into season-specific and season-agnostic objects.
 * @param {Array} [mosaicV3Events=[]] - All Mosaic V3 events received
 * @return {Object}
 */
export function isolateMosaicLayers(mosaicV3Events = []) {
  return {
    seasonSpecific: extractLayers(
      mosaicV3Events,
      SEASON_SPECIFIC_MOSAIC_V3_IDS
    ),
    seasonAgnostic: extractLayers(
      mosaicV3Events,
      SEASON_AGNOSTIC_MOSAIC_V3_IDS
    ),
  };
}
